import React, { useCallback } from "react";
import { get as commsGet } from "../../../service/comms/commsApi";
import { getTicketActivity } from "../../../service/comms/supportService";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import { useCachedList } from "../../../hooks/useCachedList";
import { supportTicketCache } from "../../../utils/listCache/caches";
import { toList, formatAge } from "./supportUtils";
import { ACTIVITY_EVENT_LABELS } from "./supportEnums";

/** How many trail entries to load. */
const ACTIVITY_LIMIT = 50;

/** Event types worth colouring — everything else reads as ordinary history. */
const EVENT_TONE = {
  SLA_BREACHED: "text-danger-dark",
  ESCALATED: "text-danger-dark",
  RESOLVED: "text-success",
  CSAT_RECEIVED: "text-success",
  MERGED: "text-gray-400",
};

/**
 * A one-line summary of what actually changed, pulled out of the event's
 * payload. Returns an empty string when there is nothing extra worth showing.
 *
 * @param {Object} event
 * @returns {string}
 */
const describePayload = (event) => {
  const p = event?.payload;
  if (!p || typeof p !== "object") return "";
  if (event.event_type === "SLA_BREACHED") {
    return p.clock === "next" ? "next-response clock" : "first-response clock";
  }
  const parts = [];
  if (p.from != null || p.to != null) parts.push(`${p.from ?? "—"} → ${p.to ?? "—"}`);
  if (p.reason) parts.push(p.reason);
  if (p.tags) parts.push(Array.isArray(p.tags) ? p.tags.join(", ") : String(p.tags));
  if (p.rating != null) parts.push(`★ ${p.rating}`);
  return parts.join(" · ");
};

/**
 * The ticket's audit trail — created, routed, assigned, breached, merged,
 * resolved, CSAT — newest first.
 *
 * `actor_user_id: null` means nobody did it: the SLA sweep, the round-robin
 * router, or a snooze waking up. Those render as "System".
 *
 * @param {Object} props
 * @param {string|null} props.roomId - Room id backing the ticket.
 */
export function TicketActivity({ roomId }) {
  const { getClientName } = useCommsIdentity();

  const fetcher = useCallback(
    () =>
      getTicketActivity(commsGet, roomId, { limit: ACTIVITY_LIMIT }).then((payload) =>
        toList(payload, ["events", "items", "data"])
      ),
    [roomId]
  );

  const { data, loading, error } = useCachedList({
    cache: supportTicketCache,
    key: roomId ? `activity:${roomId}` : null,
    fetcher,
    enabled: Boolean(roomId),
  });

  const events = data || [];

  if (loading && events.length === 0) {
    return <p className="text-xs text-gray-400">Loading activity…</p>;
  }
  if (error && events.length === 0) {
    return (
      <p className="text-xs text-danger-dark">
        {error.message || "Unable to load the activity trail"}
      </p>
    );
  }
  if (events.length === 0) {
    return <p className="text-xs text-gray-400">No activity recorded yet.</p>;
  }

  return (
    <ol className="space-y-2.5">
      {events.map((event, i) => {
        const label = ACTIVITY_EVENT_LABELS[event.event_type] || event.event_type;
        const detail = describePayload(event);
        const actor = event.actor_user_id
          ? getClientName(event.actor_user_id) || "An agent"
          : "System";
        return (
          <li
            key={event.id || i}
            className="border-l-2 border-gray-200 pl-3 text-xs leading-snug"
          >
            <p className={`font-semibold ${EVENT_TONE[event.event_type] || "text-gray-700"}`}>
              {label}
            </p>
            {detail && <p className="text-gray-600">{detail}</p>}
            <p className="text-[10px] text-gray-400">
              {actor}
              {event.created_at && ` · ${formatAge(event.created_at)} ago`}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

export default TicketActivity;
